import type { HallOfFameEntry } from "@core/certificates/types";
import { normalizeLatinDigits } from "@core/shared/utils/locale-formatters";
import { cn } from "@design-system/utils";
import { plainTrans } from "@mongez/localization";
import { rankMedal } from "../utils/certificate-presentation";
import { RankBadge } from "./RankBadge";
import { UserAvatar } from "@app/shared/components/UserAvatar";

type HallOfFameCardProps = {
  entry: HallOfFameEntry;
  className?: string;
};

/**
 * Hall-of-fame tile: certificate thumbnail, medal and the student's display
 * name (the API already applies the guest name/avatar rules).
 *
 * @example
 * <HallOfFameCard entry={entry} />
 */
export function HallOfFameCard({ entry, className }: HallOfFameCardProps) {
  const medal = rankMedal(entry.rank);
  const thumbnail = entry.thumbnail || entry.image;
  // plainTrans: the app's default converter returns JSX, but aria-label needs text.
  const label = plainTrans("certificates.hallOfFame.entryLabel", {
    name: entry.studentName,
    title: entry.title,
  });

  return (
    <figure
      aria-label={normalizeLatinDigits(
        entry.rankLabel ? `${label}، ${entry.rankLabel}` : label,
      )}
      className={cn(
        "flex flex-col overflow-hidden rounded-3xl border-2 border-b-[6px] border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900",
        className,
      )}
    >
      <div className="relative aspect-[842/596] w-full overflow-hidden bg-gradient-to-br from-amber-100 to-violet-100 dark:from-amber-950 dark:to-violet-950">
        {thumbnail ? (
          <img
            src={thumbnail}
            alt=""
            loading="lazy"
            className="size-full object-cover"
          />
        ) : null}
        {medal ? (
          <span
            aria-hidden="true"
            className="absolute -bottom-0.5 end-2 text-4xl drop-shadow-md"
          >
            {medal.emoji}
          </span>
        ) : null}
      </div>
      <figcaption className="flex items-center gap-3 p-3">
        {/* Initials when there is no photo or it fails to load (never a broken image). */}
        <UserAvatar
          name={entry.studentName}
          imageUrl={entry.avatar}
          seed={entry.studentName}
          className="size-10 rounded-2xl text-base font-black ring-amber-300 dark:ring-amber-700"
        />
        <div className="min-w-0 flex-1">
          <p className="truncate font-black text-slate-900 dark:text-white">
            {entry.studentName}
          </p>
          <p className="truncate text-xs font-bold text-slate-500 dark:text-slate-400">
            {entry.title}
          </p>
        </div>
        <RankBadge
          rank={entry.rank}
          rankLabel={entry.rankLabel}
          size="sm"
          className="hidden shrink-0 sm:inline-flex"
        />
      </figcaption>
    </figure>
  );
}
